import { EntityStatePayload, PayloadData } from '../api';
import { World } from '../World';

/**
 * Build an EntityStatePayload for an entity from the given world.
 * Collects all components and formats them for network transmission and persistence.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildEntitySnapshot(world: World<any>, entityId: number): EntityStatePayload {
  const components = world.getEntity(entityId);
  const payloadComponents: { [key: string]: PayloadData } = {};
  if (components) {
    for (const component of components.values()) {
      // Components satisfy PayloadData interface (typeName + data fields)
      payloadComponents[component.typeName] = component as PayloadData;
    }
  }
  return {
    type: 'entityState',
    entityId,
    components: payloadComponents,
    reason: 'sync',
  };
}

/**
 * Deep clones an entity and its hierarchy from a source world to a target world.
 * Remaps all internal entity IDs to point to matching clones in the destination world.
 *
 * @param sourceWorld - The world to clone from
 * @param sourceId - The entity ID to clone
 * @param targetWorld - The world to clone into
 * @param idMap - Internal recursion state for remapping IDs (do not pass manually)
 * @returns The new entity ID in the target world
 */

export function cloneEntity(
  sourceWorld: Pick<World<any>, 'getChildren' | 'getEntity'>, // eslint-disable-line @typescript-eslint/no-explicit-any
  sourceId: number,
  targetWorld: Pick<
    World<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    'withoutTracking' | 'createEntity' | 'addComponent' | 'addChildren'
  >,
  idMap: Map<number, number> = new Map(),
): number {
  return targetWorld.withoutTracking(() => {
    // 1. Recursive Depth-First Cloning
    // We clone children BEFORE the parent so that when we copy the parent's components,
    // we can immediately remap any slots/references to the new child IDs.
    const children = sourceWorld.getChildren(sourceId);
    for (const childId of children) {
      if (!idMap.has(childId)) {
        cloneEntity(sourceWorld, childId, targetWorld, idMap);
      }
    }

    // 2. Clone the Entity itself
    const sourceEntity = sourceWorld.getEntity(sourceId);
    if (!sourceEntity) {
      throw new Error(`cloneEntity: Source entity ${sourceId} not found`);
    }

    const targetId = targetWorld.createEntity();
    idMap.set(sourceId, targetId);

    // 3. Copy & Remap Components
    for (const comp of sourceEntity.values()) {
      // Skip Children/ChildOf as these are managed by targetWorld.addChildren()
      if (comp.typeName === 'Children' || comp.typeName === 'ChildOf') continue;

      // Deep copy component data
      const clone = JSON.parse(JSON.stringify(comp));

      // Remap IDs within components
      if (comp.typeName === 'Equipment') {
        const equip = clone as { slots: Record<string, number | null> };
        for (const slot in equip.slots) {
          const val = equip.slots[slot];
          if (typeof val === 'number' && idMap.has(val)) {
            equip.slots[slot] = idMap.get(val)!;
          }
        }
      }

      if (comp.typeName === 'Inventory') {
        const inventory = clone as { slots: (number | null)[] };
        if (Array.isArray(inventory.slots)) {
          for (let i = 0; i < inventory.slots.length; i++) {
            const val = inventory.slots[i];
            if (typeof val === 'number' && idMap.has(val)) {
              inventory.slots[i] = idMap.get(val)!;
            }
          }
        }
      }

      targetWorld.addComponent(targetId, clone);
    }

    // 4. Re-establish Hierarchy Links in target world
    const targetChildren = children
      .map((id) => idMap.get(id))
      .filter((id): id is number => id !== undefined);
    if (targetChildren.length > 0) {
      targetWorld.addChildren(targetId, targetChildren);
    }

    return targetId;
  });
}

/**
 * Infer a human-readable entity type from its components.
 * Supports both Map (server-side ECS) and Record (serialization payloads).
 */
export function inferEntityType(
  components: Map<string, unknown> | Record<string, unknown>,
): string {
  const has = (name: string) =>
    components instanceof Map ? components.has(name) : name in components;

  if (has('PlayerControlled')) return 'Player';
  if (has('Item')) return 'Item';
  if (has('GroundItem')) return 'GroundItem';
  if (has('Interactable')) return 'Interactable';
  if (has('TriggerZone')) return 'Trigger';
  return 'Entity';
}
